'use client'

import { useState, useEffect } from 'react'

const CONSENT_KEY = 'truelore_cookie_consent'
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? ''

function getConsent(): string | null {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

function setConsentValue(value: 'accepted' | 'declined') {
  try {
    localStorage.setItem(CONSENT_KEY, value)
  } catch {
    // localStorage unavailable
  }
}

function loadGTM() {
  if (document.getElementById('gtm-script')) return

  const script = document.createElement('script')
  script.id = 'gtm-script'
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`
  document.head.appendChild(script)

  const noscript = document.createElement('noscript')
  noscript.id = 'gtm-noscript'
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
  document.body.insertBefore(noscript, document.body.firstChild)
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const consent = getConsent()
    if (consent === 'accepted') {
      loadGTM()
    } else if (consent === null) {
      // Defer state update to avoid synchronous setState in effect
      requestAnimationFrame(() => setVisible(true))
    }
  }, [])

  const handleAccept = () => {
    setConsentValue('accepted')
    loadGTM()
    setVisible(false)
  }

  const handleDecline = () => {
    setConsentValue('declined')
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      data-component="CookieConsent"
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-desc"
      className="fixed bottom-0 inset-x-0 z-50 p-4 md:p-6 animate-fade-up"
    >
      <div className="max-w-3xl mx-auto bg-navy dark:bg-dark-card border border-cream/10 rounded-2xl p-6 shadow-2xl">
        <p
          id="cookie-desc"
          className="text-sm text-cream/80 mb-4 leading-relaxed"
        >
          We use cookies and similar technologies to analyze site traffic and improve your
          experience. By clicking &ldquo;Accept&rdquo;, you consent to the use of analytics
          cookies. You can decline and still use the site fully. See our{' '}
          <a
            href="https://true-lore.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-carolina underline hover:text-carolina/80"
          >
            Privacy Policy
          </a>{' '}
          for details.
        </p>
        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={handleDecline}
            className="text-sm font-medium text-cream/60 hover:text-cream px-5 py-2.5 rounded-full border border-cream/20 hover:border-cream/40 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="text-sm font-bold text-cream bg-carolina px-6 py-2.5 rounded-full hover:scale-[1.03] active:scale-[0.97] transition-all"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
